<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import { currencyFormatter } from 'next/message/bubbles/Lazada/helper/formatter.js';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import LoadingState from 'components/widgets/LoadingState.vue';
import EmptyMessage from 'components/widgets/EmptyMessage.vue';

const props = defineProps({
  currentChat: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const keyword = ref('');
const store = useStore();

const uiFlags = useMapGetter('lazada/getUIFlags');
const products = useMapGetter('lazada/searchProducts');

const productImage = product => {
  return product.meta?.image_urls?.[0] || null;
};

const productPrice = product => {
  const price = product.meta?.price;
  return currencyFormatter(price || 0);
};

const productStatusMapping = product => {
  switch (product.status) {
    case 'active':
      return {
        text: t('CONVERSATION.LAZADA.PRODUCTS.STATUS.ACTIVE'),
        class: 'bg-woot-600',
      };
    case 'inactive':
      return {
        text: t('CONVERSATION.LAZADA.PRODUCTS.STATUS.INACTIVE'),
        class: 'bg-red-600',
      };
    default:
      return {
        text: product.status,
        class: 'bg-orange-300',
      };
  }
};

const searchProducts = () => {
  store.dispatch('lazada/searchProducts', {
    conversationID: props.currentChat.id,
    keyword: keyword.value,
  });
};

const sendProduct = product => {
  store.dispatch('lazada/sendProductMessage', {
    conversationID: props.currentChat.id,
    itemID: product.itemId,
  });
};

onMounted(() => {
  searchProducts();
});
</script>

<template>
  <div class="flex flex-col h-full bg-slate-50 dark:bg-slate-800">
    <div id="search-product">
      <input
        v-model="keyword"
        type="text"
        :placeholder="t('CONVERSATION.LAZADA.PRODUCTS.SEARCH')"
        @keyup.enter="searchProducts"
      />
      <Icon
        class="absolute top-3 right-5 text-slate-500 dark:text-slate-500"
        icon="i-lucide-search"
      />
    </div>
    <div class="flex-1 overflow-y-auto">
      <LoadingState v-if="uiFlags.isSearchingProducts" />
      <EmptyMessage
        v-else-if="!products.length"
        :message="t('CONVERSATION.LAZADA.PRODUCTS.EMPTY_MESSAGE')"
      />
      <ul v-else class="p-2">
        <li v-for="product in products" :key="product.sku" class="product">
          <p
            class="w-fit rounded-full text-white px-2 py-0.5 text-xs font-semibold"
            :class="productStatusMapping(product).class"
          >
            {{ productStatusMapping(product).text }}
          </p>
          <div class="flex flex-row pb-2">
            <div class="flex-none w-12 h-12">
              <a
                :href="product.meta?.external_url"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  v-if="productImage(product)"
                  :src="productImage(product)"
                  :alt="product.name"
                  class="rounded-lg object-cover"
                />
              </a>
            </div>
            <div class="flex-auto ps-2 flex-col overflow-hidden">
              <a
                v-tooltip.top="product.name"
                :href="product.meta?.external_url"
                target="_blank"
                rel="noopener noreferrer"
                class="line-clamp-1 w-full text-xs mb-0"
              >
                {{ product.name }}
              </a>
              <div class="flex flex-col">
                <span class="flex-1 text-slate-500 text-xs">
                  {{ product.sku }}
                </span>
                <span class="flex-1 text-slate-500 text-xs">
                  {{ t('CONVERSATION.LAZADA.PRODUCTS.STOCK') }}
                  {{ product.meta?.stock }}
                </span>
              </div>
              <div class="flex-row text-end">
                <span
                  v-if="productPrice(product)"
                  class="font-semibold flex-1 items-center text-woot-600 ms-2"
                >
                  {{ productPrice(product) }}
                </span>
              </div>
            </div>
          </div>
          <div
            class="flex flex-row border-t border-slate-200 dark:border-slate-600 border-dashed pt-2"
          >
            <div>
              <small
                class="border bg-white px-2 py-0.5 rounded-full text-xs me-1 border-woot-600 text-woot-600 dark:bg-slate-900 dark:text-slate-300 dark:border-slate-300"
              >
                {{ product.meta?.brand_name }}
              </small>
            </div>
            <div class="flex-auto text-end">
              <button
                class="ms-2 text-white bg-woot-600 rounded-md py-1 px-5 text-sm hover:bg-woot-400 cursor-pointer"
                :disabled="product.status !== 'active'"
                @click="sendProduct(product)"
              >
                {{ t('CONVERSATION.LAZADA.SEND') }}
              </button>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
#search-product {
  @apply bg-white dark:bg-slate-900 px-2 py-1 relative border-b border-slate-100 dark:border-slate-800;

  input {
    @apply border-none rounded-3xl text-xs mb-0 outline-none pe-9 ps-5 py-0 h-8 dark:bg-slate-800;
  }
}

.product {
  @apply w-full mb-2 relative bg-white dark:bg-slate-900 rounded-md shadow-sm p-2;

  &:hover {
    .product-icon {
      @apply hidden;
    }
  }
}
</style>
